import {BookmarkDateRange} from 'common/widgets/date-range-picker/models/types';
import {PropertyFilterAttributes} from 'common/widgets/property-filter-menu/models/property-filter';
import {Segment} from 'common/widgets/query-block/segment-menu/types';
import {CohortFilterTree} from 'common/widgets/query-block/filter-menu/types';
import {FiltersOperator} from 'common/widgets/query-entry-section/types';
import {Segfilter} from 'common/widgets/property-filter-menu/models/segfilter';

/** Mode of querying. 'compounded' is deprecated as of 2020-03-11. */
export type RetentionType = `compounded` | `birth` | `addiction`;
export type ChartType = `line` | `table`;
export type DateUnit = `day` | `week` | `month`;

export type LoadingStatus = `loading` | `complete` | `error` | `dataClassificationError` | `missingMappedDataGroup`;

export enum RetentionChartType {
  Table = `table`,
  Line = `line`,
}

export interface SelectedSegment {
  name: string;
  color: string;
}

/**
 * Relative date range.
 *
 * This was the persisted bookmark format from 2014-10-23 to 2019-05-28.
 *
 * @example {to: 0, from: -14} // last 2 weeks
 */
export type RelativeRollingDateRange = {
  to: number;
  from: number;
};

export interface RetentionBookmarkParams {
  /**
   * Date range for the query.
   *
   * Bookmarks saved after 2019-05-28 have BookmarkDateRange
   * Bookmarks saved after 2014-10-23 have RelativeRollingDateRange
   * Bookmarks saved before do not have a date_range.
   */
  date_range?: BookmarkDateRange | RelativeRollingDateRange;
  date_unit: DateUnit;
  /**
   * Event for birth retention.
   *
   * Some actual null, some string 'null', some real strings.
   * */
  born_event?: null | `null` | string;
  /**
   * Main query event.
   *
   * Sometimes set to empty string between 2012-01-17 and 2015-01-10.
   * Sometimes set to null between 2014-11-25 and 2016-02-18.
   * Rarely set to 'null' between 2014 - 2015 (6 times).
   */
  event: string | '' | null;

  // it looks like in the past only a single selection was allowed.
  // post QB, this is a full cohort tree
  // eslint-disable-next-line @typescript-eslint/ban-types
  filter_by_cohort?: CohortFilterTree | {};
  /** new format for breakdowns */
  group_by?: Segment[];
  /**
   * deprecated way of storing breakdowns as arb selector string
   *
   * @example `properties["$browser"]`
   */
  on?: string;

  filters?: Segfilter[];
  filters_op?: FiltersOperator;
  born_filters?: Segfilter[];
  born_filters_op?: FiltersOperator;

  /** deprecated way of storing cohorts for either filter or breakdown */
  cohorts?: Array<{id: number; name: string}>;
  include_negated_cohorts?: boolean;
  segmentation_event?: string;

  /**
   * which datagroup is active.
   */
  data_group_id?:
    | `` // none selected
    | `0` // none selected (deprecated way)
    | string // stringified number
    | null; // deprecated

  retention_type: RetentionType;
  chart_type?: ChartType;
  cumulative?: boolean;
  unbounded_retention?: boolean;
  percentage_view?: boolean;
  sort_column?: string;
  sort_desc?: boolean;

  /** A link back to the report. For dashboard cards. */
  url_hash?: string;

  filter_by_event?: // eslint-disable-next-line @typescript-eslint/ban-types
  | {}
    | {
        operator: string;
        children: Array<
          | {
              event: PropertyFilterAttributes;
            }
          | {
              user: PropertyFilterAttributes;
            }
        >;
      };
}

export interface RawCell {
  absolute_drop_off_value: number;
  absolute_value: number;
  cohort_size: number;
  complete_date: string | false;
  display_value: string;
  entered_value: string;
  incomplete: boolean;
  percent: number;
  percent_drop_off_value: string;
  percent_value: string;
  selectable: boolean;
  value: number;
}
export interface TableCell extends RawCell {
  backgroundColor: string;
  textColor: string;
  clickable?: boolean;
  tooltip?: string;
}

export type CellType = TableCell | RawCell;

/**
 * TableRow for the old retention-table
 */

export interface FormattedRow<T extends CellType> {
  date?: Date;
  /** number of users who entered this bucket */
  cohort_size: number;
  /** cohort_size, but commaized and ready for display */
  display_cohort_size: string;
  /** raw_head, but truncated to fit in a cell*/
  display_head: string;
  /** stringified head */
  raw_head: string;
  /** identifier for the row: Date if unsegmented, segment string if segmented */
  head: Date | string;
  /** actual retention data */
  data: T[];
}

export function isRelativeRollingDateRange(dateRange: any): dateRange is RelativeRollingDateRange {
  if (!dateRange) {
    return false;
  }
  if (!(`type` in dateRange) && (`to` in dateRange || `from` in dateRange)) {
    return true;
  }

  return false;
}
export function isRetentionType(queryType: string): queryType is RetentionType {
  return [`addiction`, `birth`, `compounded`].includes(queryType);
}
